import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Zap } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import ParticleField from "@/components/ParticleField";

const HERO_IMG =
  "https://images.unsplash.com/photo-1637166185518-058f5896a2e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwc29mdHdhcmUlMjBhcmNoaXRlY3R1cmUlMjB0ZWNoJTIwZGFyayUyMHNlcnZlciUyMHJvb20lMjBhYnN0cmFjdCUyMDNkJTIwbWVzaHxlbnwwfHx8fDE3ODgyOTkzMTl8MA&ixlib=rb-4.1.0&q=85";

const Hero = ({ booted, onQuote }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-[72px]">
      <div className="grid-bg absolute inset-0" />
      <ParticleField />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-cy/10 blur-[140px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[400px] w-[400px] rounded-full bg-em/10 blur-[140px]" />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-12">
        <div className="grid items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, x: -24 }}
              animate={booted ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mb-8 font-mono text-xs uppercase tracking-[0.28em] text-cy"
              data-testid="hero-label"
            >
              {t.hero.label}
            </motion.p>

            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl" data-testid="hero-heading">
              {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "text-cy text-glow" : ""}`}
                    initial={{ y: "115%" }}
                    animate={booted ? { y: 0 } : {}}
                    transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={booted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
              data-testid="hero-subheading"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={booted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-6 flex items-start gap-3 border-l-2 border-amber/70 pl-4"
            >
              <Zap className="mt-0.5 h-4 w-4 shrink-0 text-amber" />
              <p className="text-sm leading-relaxed text-slate-300" data-testid="hero-urgency">{t.hero.urgency}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={booted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-request-quote-btn"
                onClick={onQuote}
                className="group flex items-center gap-2 rounded-full bg-cy px-7 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-em hover:shadow-[0_0_35px_rgba(0,240,255,0.4)]"
              >
                {t.hero.ctaPrimary}
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                data-testid="hero-explore-services-btn"
                onClick={() => scrollToId("servicios")}
                className="group flex items-center gap-2 rounded-full border border-cy/30 px-7 py-4 font-display text-base font-semibold text-slate-200 transition-all duration-300 hover:border-cy hover:bg-cy/10"
              >
                {t.hero.ctaSecondary}
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={booted ? { opacity: 1 } : {}}
              transition={{ duration: 1, delay: 1.25 }}
              className="mt-14 flex gap-10"
            >
              {t.hero.stats.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <p className="font-display text-3xl font-extrabold text-cy">{s.v}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.l}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={booted ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <div className="relative overflow-hidden rounded-xl border border-cy/20 shadow-[0_0_60px_rgba(0,240,255,0.12)]" data-testid="hero-image-frame">
              <img src={HERO_IMG} alt="Quantum Century — digital infrastructure" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/20" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-cy">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-em" />
                {t.hero.frameTag}
              </div>
              <span className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-cy" />
              <span className="absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-cy" />
              <span className="absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-cy" />
              <span className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-cy" />
            </div>
          </motion.div>
        </div>
      </motion.div>

      <p className="pointer-events-none absolute -bottom-6 left-0 select-none whitespace-nowrap font-display text-[18vw] font-black leading-none text-stroke-cy opacity-20" aria-hidden="true">
        QUANTUM
      </p>
    </section>
  );
};

export default Hero;
